import { useEffect } from 'react';

export { useModalState } from './hooks/useModalState';
export { useIsMobile } from './hooks/useIsMobile';

export function useMergeRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    }
  };
}

interface AutoSelectOptions<T> {
  ref: React.RefObject<HTMLSelectElement | null>;
  options: T[];
  getValue: (option: T) => string;
  enabled?: boolean;
  preferredValue?: string;
}

export function useAutoSelectSingleOption<T>({
  ref,
  options,
  getValue,
  enabled = true,
  preferredValue,
}: AutoSelectOptions<T>) {
  useEffect(() => {
    const select = ref.current;
    if (!enabled || !select || select.value) return;

    if (preferredValue) {
      const hasPreferredOption = options.some((option) => getValue(option) === preferredValue);
      if (hasPreferredOption) {
        select.value = preferredValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    if (options.length === 1) {
      select.value = getValue(options[0]);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [options, enabled, getValue, preferredValue, ref]);
}
